import React, { useContext, useEffect } from 'react';
import { Header } from '../../../libs/gs-component-right-rail';
import { useManageItem } from '../../../helpers/useManageItem';
import { PageItemProps } from '../BasicItem';
import { validationSchema } from './schema';
import { DressCodeFormValues } from './types';
import { useHistory, useLocation } from 'react-router-dom';
import Form from './Form';
import { Formik, FormikProps } from 'formik';
import { useDressCodeAutoFill } from './useDressCodeAutoFill';
import { useAttireCategories } from '../../../hooks/useAttireCategories';
import styles from './Item.module.scss';
import {
  useCreateDressCodeItemMutation,
  useUpdateDressCodeItemMutation,
  useDeleteDressCodeItemMutation,
  useDressCodeQuery,
  DressCodeItemsFragment,
} from '../../../generated/graphql';
import Honeybadger from 'honeybadger-js';
import addItemToPage from '../../../helpers/apolloCache/addItemToPage';
import deletePageItem from '../../../helpers/apolloCache/deletePageItem';
import { pagesQuery } from '../../LeftContainer/graphql';
import useWeddingDayId from '../../../helpers/useWeddingDayId';
import { TrackingContext } from '../../../TrackingContext';
import { getDressCodeTrackingProperties } from './constants';

const initialValues: DressCodeFormValues = {
  attireCategoryId: '',
  title: '',
  description: '',
  showArticles: true,
  showAffiliates: true,
  eventId: '',
};

interface ItemProps extends PageItemProps {
  headerText?: string;
}

const Item: React.FC<ItemProps> = ({ onSaveHandler, headerText = 'Dress Code' }) => {
  const { pageId, itemId, isNew, close: baseClose } = useManageItem();
  const { track } = useContext(TrackingContext);
  const { pathname } = useLocation();
  const [create, { loading: creating }] = useCreateDressCodeItemMutation();
  const [update, { loading: updating }] = useUpdateDressCodeItemMutation();
  const [destroy, { loading: deleting }] = useDeleteDressCodeItemMutation();
  const { attireCategories, loading: loadingCategories, error: categoriesError } = useAttireCategories();

  const { data } = useDressCodeQuery({
    variables: { pageId, id: itemId || -1 },
    skip: isNew,
  });

  const item = data?.page?.items?.find((i: any) => i && i.__typename === 'WWS_DressCodeItem' && i.id === itemId) as
    | DressCodeItemsFragment
    | undefined;

  // Report error if attire categories fail to load
  useEffect(() => {
    if (categoriesError) {
      Honeybadger.notify(categoriesError);
    }
  }, [categoriesError]);

  // Custom close function with dress code tracking
  const close = (pageItem?: DressCodeItemsFragment) => {
    if (pageItem) {
      const source = pathname.includes('preview') ? 'preview' : 'dashboard';
      track(`Item ${isNew ? 'Added' : 'Edited'}`, {
        action: isNew ? 'add' : 'edit',
        actionName: `${isNew ? 'Add' : 'Edit'} Page Item`,
        page: 'Dress Code',
        item: 'DressCodeItem',
        source,
        ...getDressCodeTrackingProperties(pageItem),
      });
    }
    baseClose(pageItem);
  };

  const history = useHistory();
  const goBack = () => history.goBack();
  const { handleDressCodeTypeChange } = useDressCodeAutoFill(attireCategories);
  const eventId = useWeddingDayId();

  const handleSubmit = async (values: DressCodeFormValues) => {
    const attrs: any = {
      pageId,
      description: values.description || null,
      attireDescription: values.title || null,
      attireCategoryId: values.attireCategoryId || null,
      eventId: (item && item.eventId) || eventId || null,
      showArticles: !!values.showArticles,
      showAffiliates: !!values.showAffiliates,
    };

    try {
      if (itemId && !isNew) {
        // update
        await update({
          variables: { attributes: { ...attrs, id: itemId } },
          update: (_cache, { data }) => close(data?.updateDressCodeItem),
        });
      } else {
        // create
        await create({
          variables: { attributes: attrs },
          update: (cache, { data }) => {
            addItemToPage<DressCodeItemsFragment | undefined>(cache, pageId, data?.createDressCodeItem, close);
          },
          refetchQueries: [{ query: pagesQuery }],
        });
      }

      if (onSaveHandler) onSaveHandler();
    } catch (error) {
      console.error('Error saving dress code item:', error);
    }
  };

  const discardOrDelete = async () => {
    if (!itemId) {
      close();
      return;
    }

    try {
      await destroy({
        variables: { attributes: { id: itemId, pageId } },
        update(cache) {
          deletePageItem({ cache, pageId, itemId });
        },
        refetchQueries: [{ query: pagesQuery }],
      });
    } catch (error) {
      Honeybadger.notify(error);
    }

    close();
  };

  const formInitialValues: DressCodeFormValues = item
    ? {
        attireCategoryId: item.attireCategoryId || '',
        title: item.attireDescription || '',
        description: item.description || '',
        showArticles: !!item.showArticles,
        showAffiliates: !!item.showAffiliates,
        eventId: item.eventId || eventId || '',
      }
    : initialValues;

  return (
    <div className={styles.dressCodeItem} data-testid="dress-code-item">
      <Header leftButtonText="Back" leftButtonOnClick={goBack} as="h1">
        {headerText}
      </Header>
      <Formik<DressCodeFormValues>
        initialValues={formInitialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue, submitForm, values }: FormikProps<DressCodeFormValues>) => (
          <Form
            values={values}
            setFieldValue={setFieldValue}
            submitForm={submitForm}
            loading={updating || creating || deleting || loadingCategories}
            onClose={close}
            handleDressCodeTypeChange={handleDressCodeTypeChange}
            discardOrDelete={discardOrDelete}
            isNew={isNew}
            attireCategories={attireCategories}
          />
        )}
      </Formik>
    </div>
  );
};

export default Item;
